/**
 * 历史表里「这一期有没有我」。
 *
 * <p>语义沿用游戏页那排最近结果**已经在用**的三档（2026-08-19 评审确定，
 * 见 `ds-components.css` 的 `.milhar-result.is-played` / `.is-won`）：
 * 金 = 我中了、绿 = 我玩了没中、无 = 我没玩。两屏用同一套，点进来的人才认得出。
 */

export type MineMark = 'won' | 'played' | 'none';

/**
 * 这一行该标成哪一档。
 *
 * <p><b>三个字段全部可缺席</b> —— 前端要能先于后端上线。缺席时一律退回
 * `none` / `played`，**绝不**因为读不到派彩就谎报中奖。
 *
 * <p><b>`myPayout` 的格式约定</b>：解析走 `Number()`，要求后端吐十进制小数点
 * （如 `"25.00"`）。**不许出现千分位逗号或欧式小数逗号**（如 `"25,00"`）——
 * `Number("25,00")` 是 `NaN`，会被 `Number.isFinite` 挡掉退回 `played`，一个真正
 * 中奖的行会被静默显示成「参与未中」。这条约定在字段真正上线前先钉死，别等
 * 出了线上事故才发现契约没写清楚。
 */
export function mineMark(row: { mine?: boolean; myPayout?: string | null }): MineMark {
  if (row.mine !== true) return 'none';
  const payout = Number(row.myPayout);
  return Number.isFinite(payout) && payout > 0 ? 'won' : 'played';
}

/**
 * 这一批行里，有没有哪一期真的是我参与的。
 *
 * <p>后端字段可能整批缺席（前端先于后端上线，见 `api.ts` 里 `MilharStats.history`
 * 的注释）。此时 `mineMark()` 对每一行都会退回 `'none'` —— 但那是「不知道」，
 * 不是「真的没参与」。登录玩家看见一整列灰条断言自己一期都没玩过，这是在替
 * 玩家的钱撒谎（2026-09-11 评审 Finding 1）。
 *
 * <p><b>判定条件不能是 `mine !== undefined`。</b> `MilharController` 对**每一个
 * 登录玩家**都会写这个字段 —— 没下注就是 `false`，不是缺席（`MilharController
 * .java:175-186`，只有访客整组省略）。所以那道判断对任何登录玩家都恒为真，
 * 这条防线从来没生效过：一个最近 100 期都没下注的玩家打开 History，看到的
 * 正是上面那一整列灰条（2026-09-17 复核，与 `shared/raffle/historyRowView.ts`
 * 的 `hasMineData` 在 09-13 修掉的是同一个错）。
 *
 * <p>必须看**有没有哪一行的 `mine` 是 `true`**，不是这个字段存不存在。整列
 * 收起之后，那句「最近 %n% 期你没有下注」（`historyLegendView` 的 `noRecent`）
 * 接管这块位置 —— 收起不等于什么都不说。
 *
 * <p>**必须扫全部行**，不能只看第一行 —— 分页/窗口边界上完全可能出现某几行
 * 来自旧缓存、某几行来自新响应的混合情况。
 */
export function hasMineData(rows: { mine?: boolean }[]): boolean {
  return rows.some((row) => row.mine === true);
}
